import { useMemo, useState } from "react";
import { useNotification } from "../../../../../hooks/useNotification";
import { usePermission } from "../../../../../hooks/usePermissions";
import { ASN_CANCEL, ASN_CONFIRM } from "../../../../../constant/permissions";
import {
  useBulkASNStatusMutation,
  useSyncASNMutation,
} from "../../../../../store/apiSlice/shipmentApiSlice";
import { componentWithCondition } from "../../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ButtonSpinner } from "../../../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

/**
 * ASN list toolbar actions: Sync + bulk Confirm / Cancel.
 *
 * @param {{
 *   selectedOrderId: number[],
 *   setSelectedOrder: function,
 *   selectedOrderFromListview: Object[],
 *   callbacks?: { getConsignmentByRule?: function }
 * }} props
 */
const AsnAction = ({ selectedOrderId, setSelectedOrder, callbacks }) => {
  const notify = useNotification();
  const permission = usePermission();
  const [bulkStatus, { isLoading: isBulkLoading } = {}] = useBulkASNStatusMutation();
  const [syncASN, { isLoading: isSyncLoading } = {}] = useSyncASNMutation();
  const [activeBulkTarget, setActiveBulkTarget] = useState(null);

  const count = selectedOrderId?.length || 0;
  const overLimit = count > 100;
  const canBulk = count > 0 && !overLimit && !isBulkLoading;
  const reload = callbacks?.getConsignmentByRule;

  const runBulk = async status => {
    if (!canBulk) return;
    if (status === "confirmed" && !permission(ASN_CONFIRM)) {
      notify({ type: "error", message: "You do not have permission to confirm ASNs." });
      return;
    }
    if (status === "cancelled" && !permission(ASN_CANCEL)) {
      notify({ type: "error", message: "You do not have permission to cancel ASNs." });
      return;
    }

    setActiveBulkTarget(status);
    try {
      const res = await bulkStatus({ ids: selectedOrderId, status }).unwrap();
      const ok = res.successes?.length || 0;
      const fail = res.failures?.length || 0;
      notify({
        type: fail ? "warning" : "success",
        message: `Bulk ${status}: ${ok} succeeded, ${fail} failed.`,
      });
      if (fail && res.failures?.[0]?.errors?.[0]) {
        notify({ type: "error", message: res.failures[0].errors[0] });
      }
      setSelectedOrder([]);
      reload?.();
    } catch (err) {
      notify({
        type: "error",
        message: err.data?.detail || err.data?.ids?.[0] || "Bulk update failed.",
      });
    } finally {
      setActiveBulkTarget(null);
    }
  };

  const sync = async () => {
    try {
      const res = await syncASN().unwrap();
      notify({ type: "success", message: res.detail });
      reload?.();
    } catch (err) {
      notify({
        type: "error",
        message: err.data?.detail || "Something went wrong.",
      });
    }
  };

  const bulkTitle = overLimit
    ? "Select at most 100 ASNs"
    : count < 1
      ? "Select ASNs first"
      : undefined;

  const isBulkConfirming = isBulkLoading && activeBulkTarget === "confirmed";
  const isBulkCancelling = isBulkLoading && activeBulkTarget === "cancelled";

  const burgerItems = useMemo(
    () => [
      {
        key: "cancel",
        label: count > 0 ? `Cancel (${count})` : "Cancel",
        onClick: () => runBulk("cancelled"),
        disabled: !canBulk || !permission(ASN_CANCEL) || isBulkCancelling,
        title: bulkTitle || (!permission(ASN_CANCEL) ? "You do not have permission to cancel ASNs." : ""),
        isLoading: isBulkCancelling,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canBulk, count, permission, isBulkCancelling, bulkTitle, selectedOrderId],
  );

  return (
    <>
      <li className={`tableButton ${!isSyncLoading ? "activeState" : "disableState"}`}>
        <button
          onClick={sync}
          disabled={isSyncLoading}
          type="button"
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isSyncLoading && <ButtonSpinner aria-hidden="true" />}
          {isSyncLoading ? "Syncing…" : "Sync"}
        </button>
      </li>
      <li
        className={`tableButton ${
          canBulk && permission(ASN_CONFIRM) && !isBulkConfirming ? "activeState" : "disableState"
        }`}
      >
        <button
          type="button"
          onClick={() => runBulk("confirmed")}
          disabled={!canBulk || !permission(ASN_CONFIRM) || isBulkConfirming}
          title={bulkTitle || (!permission(ASN_CONFIRM) ? "You do not have permission to confirm ASNs." : "")}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isBulkConfirming && <ButtonSpinner aria-hidden="true" />}
          {isBulkConfirming ? "Confirming…" : count > 0 ? `Confirm (${count})` : "Confirm"}
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />
    </>
  );
};

export const AsnActionWithCheck = componentWithCondition(AsnAction);
